import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import Avatar from '../components/Avatar';

const statusLabels = {
  draft: 'مسودة', submitted: 'مُرسل', under_review: 'قيد المراجعة',
  approved: 'معتمد', rejected: 'مرفوض', printed: 'مطبوع'
};

export default function ParticipantsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canReview = user.role === 'admin' || user.role === 'reviewer';
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [checked, setChecked] = useState(new Set());
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [view, setView] = useState('list'); // 'list' | 'grid'

  const load = async () => {
    const { data } = await client.get('/participants', { params: { search, status } });
    setRows(data.data);
    setTotal(data.total);
    setChecked(new Set());
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const doSearch = (e) => { e.preventDefault(); load(); };

  const changeStatus = async (id, newStatus, reason) => {
    await client.patch(`/participants/${id}/status`, { status: newStatus, rejection_reason: reason });
    setSelected(null);
    setRejectReason('');
    load();
  };

  const downloadBadge = (id) => {
    window.open(`${client.defaults.baseURL}/badges/${id}/pdf?token=${localStorage.getItem('accred_token')}&_t=${Date.now()}`, '_blank');
  };

  const toggleCheck = (id) => {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setChecked(checked.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  };

  const bulkApprove = async () => {
    if (!checked.size) return;
    await client.patch('/participants/bulk/status', { ids: [...checked], status: 'approved' });
    load();
  };
  const bulkReject = async () => {
    await client.patch('/participants/bulk/status', { ids: [...checked], status: 'rejected', rejection_reason: bulkRejectReason });
    setBulkRejectOpen(false); setBulkRejectReason('');
    load();
  };
  const bulkExport = () => {
    window.open(`${client.defaults.baseURL}/participants/export?ids=${[...checked].join(',')}&token=${localStorage.getItem('accred_token')}`, '_blank');
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <form onSubmit={doSearch} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
          <div className="form-field" style={{ minWidth: 220 }}>
            <label>بحث بالاسم أو رقم البطاقة</label>
            <input value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="form-field" style={{ minWidth: 180 }}>
            <label>الحالة</label>
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">الكل</option>
              {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <button className="btn btn-primary">بحث</button>
        </form>
      </div>

      {checked.size > 0 && (
        <div className="card no-print" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--signal-tint)', borderColor: 'var(--signal)' }}>
          <b style={{ fontSize: 13 }}>{checked.size} محدد</b>
          {canReview && <button className="btn btn-success" onClick={bulkApprove}>اعتماد المحدد</button>}
          {canReview && <button className="btn btn-danger" onClick={() => setBulkRejectOpen(true)}>رفض المحدد</button>}
          <button className="btn btn-outline" onClick={bulkExport}>تصدير المحدد Excel</button>
          <button className="btn btn-outline" onClick={() => setChecked(new Set())}>إلغاء التحديد</button>
        </div>
      )}

      {bulkRejectOpen && (
        <div className="card no-print" style={{ marginBottom: 16 }}>
          <h4 style={{ marginTop: 0 }}>سبب الرفض الجماعي</h4>
          <textarea rows="3" value={bulkRejectReason} onChange={e => setBulkRejectReason(e.target.value)} style={{ width: '100%' }} />
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button className="btn btn-danger" onClick={bulkReject}>تأكيد الرفض ({checked.size})</button>
            <button className="btn btn-outline" onClick={() => setBulkRejectOpen(false)}>إلغاء</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>المشاركون ({total})</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ display: 'flex', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
              <button
                onClick={() => setView('list')}
                style={{ padding: '8px 12px', border: 'none', cursor: 'pointer', fontSize: 13, background: view === 'list' ? 'var(--signal)' : '#fff', color: view === 'list' ? '#fff' : 'var(--text)' }}
              >قائمة</button>
              <button
                onClick={() => setView('grid')}
                style={{ padding: '8px 12px', border: 'none', cursor: 'pointer', fontSize: 13, background: view === 'grid' ? 'var(--signal)' : '#fff', color: view === 'grid' ? '#fff' : 'var(--text)' }}
              >شبكة الصور</button>
            </div>
            <button className="btn btn-outline" onClick={() => window.open(`${client.defaults.baseURL}/participants/export?status=${status}&search=${search}&token=${localStorage.getItem('accred_token')}`, '_blank')}>تصدير Excel</button>
            <button className="btn btn-outline" onClick={() => window.print()}>طباعة</button>
          </div>
        </div>

        {view === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 14 }}>
            {rows.map(p => (
              <div
                key={p.id}
                style={{ textAlign: 'center', cursor: 'pointer', padding: 10, borderRadius: 10, border: '1px solid var(--line)' }}
                onClick={() => navigate(`/participants/${p.id}`)}
              >
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                  <Avatar src={p.photo_path} name={p.full_name_ar} size={72} color={p.color_code} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{p.full_name_ar}</div>
                <div style={{ marginTop: 6 }}>
                  <span className="badge" style={{ background: p.color_code, fontSize: 10 }}>{p.category_name_ar}</span>
                </div>
                <div style={{ marginTop: 4 }}>
                  <span className={`badge status-${p.status}`} style={{ fontSize: 9.5 }}>{statusLabels[p.status]}</span>
                </div>
              </div>
            ))}
            {rows.length === 0 && <div style={{ textAlign: 'center', color: '#9ca3af', gridColumn: '1/-1', padding: 20 }}>لا توجد نتائج</div>}
          </div>
        ) : (
        <table>
          <thead>
            <tr>
              <th className="no-print"><input type="checkbox" checked={rows.length > 0 && checked.size === rows.length} onChange={toggleAll} /></th>
              <th></th><th>الاسم</th><th>رقم البطاقة</th><th>الفئة</th><th>الجهة</th><th>الفعالية</th><th>الحالة</th><th className="no-print">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(p => (
              <tr key={p.id}>
                <td className="no-print"><input type="checkbox" checked={checked.has(p.id)} onChange={() => toggleCheck(p.id)} /></td>
                <td><Avatar src={p.photo_path} name={p.full_name_ar} size={34} color={p.color_code} /></td>
                <td style={{ color: 'var(--signal-dark)', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate(`/participants/${p.id}`)}>{p.full_name_ar}</td>
                <td className="mono">{p.badge_number || '—'}</td>
                <td><span className="badge" style={{ background: p.color_code }}>{p.category_name_ar}</span></td>
                <td>{p.organization_name}</td>
                <td>{p.event_name}</td>
                <td><span className={`badge status-${p.status}`}>{statusLabels[p.status]}</span></td>
                <td className="no-print" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {canReview && p.status !== 'approved' && p.status !== 'rejected' && (
                    <>
                      <button className="btn btn-success" onClick={() => changeStatus(p.id, 'approved')}>اعتماد</button>
                      <button className="btn btn-danger" onClick={() => setSelected(p.id)}>رفض</button>
                    </>
                  )}
                  {(p.status === 'approved' || p.status === 'printed') && (
                    <button className="btn btn-outline" onClick={() => downloadBadge(p.id)}>طباعة البطاقة</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="9" style={{ textAlign: 'center', color: '#9ca3af' }}>لا توجد نتائج</td></tr>
            )}
          </tbody>
        </table>
        )}
      </div>

      {selected && (
        <div className="card no-print" style={{ marginTop: 16 }}>
          <h4 style={{ marginTop: 0 }}>سبب الرفض</h4>
          <textarea rows="3" value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={{ width: '100%' }} />
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button className="btn btn-danger" onClick={() => changeStatus(selected, 'rejected', rejectReason)}>تأكيد الرفض</button>
            <button className="btn btn-outline" onClick={() => setSelected(null)}>إلغاء</button>
          </div>
        </div>
      )}
    </div>
  );
}
